import React from 'react';
import axios from 'helpers/axios';
import PropTypes from 'prop-types';

import { UserContext } from 'providers';
import { useOnFirstRender } from 'hooks';

export const CompanyContext = React.createContext();

export const defaultJobSettings = {
  hoursUntilWarning: 24,
  emailCapacity: 30,
};

const NO_ACTIVE_COMPANY = '';

const initialState = {
  companies: [],
  activeCompany: NO_ACTIVE_COMPANY, // active company id
  loaded: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT_COMPANIES':
      return {
        ...state,
        companies: action.companies,
        activeCompany: action.activeCompany,
        loaded: true,
      };
    case 'SAVE_COMPANIES':
      return {
        ...state,
        companies: action.companies,
      };
    case 'REMOVE_COMPANIES':
      return {
        ...state,
        companies: [],
        activeCompany: NO_ACTIVE_COMPANY,
        loaded: false,
      };
    case 'SET_ACTIVE_COMPANY':
      return {
        ...state,
        activeCompany: action.companyId,
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export const CompanyProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const { state: userState, saveSettings } = React.useContext(UserContext);
  const defaultCompany = userState.user.data?.settings?.defaultCompany;
  const isFirstRender = useOnFirstRender();

  React.useEffect(() => {
    if (userState.isLoggedIn) {
      fetchCompanies();
    } else if (!isFirstRender) {
      removeCompanies();
    }
  }, [userState.isLoggedIn]);

  const companiesOptions = React.useMemo(() => {
    const options = state.companies.map((company) => ({
      label: company.name,
      value: company.id,
      company,
    }));
    return options;
  }, [state.companies]);

  const fetchCompanies = async (forceUpdate = false, select = 'name,member,settings') => {
    if (state.companies?.length && !forceUpdate) {
      return state.companies;
    }

    try {
      const response = await axios(
        `/companies${userState.isAdmin ? '/all' : ''}/?$top=-1&$select=${select}`
      );
      if (response.status === 200) {
        const companies = response.data.value;
        let activeCompany = defaultCompany;
        if (!activeCompany || !companies.find((company) => company.id === activeCompany)) {
          activeCompany = companies[0]?.id || NO_ACTIVE_COMPANY;
        }
        initCompanies(companies, activeCompany);
        return companies;
      }
    } catch (err) {
      console.error(err);
    }

    return [];
  };

  /**
   * Rather than using saveCompanies + setActiveCompany which will trigger 2 re-renders
   * it's better to set/save them at the same time (which is the purpose of this function)
   */
  const initCompanies = (companies, activeCompany) => {
    dispatch({ type: 'INIT_COMPANIES', companies, activeCompany });
  };

  const saveCompanies = (companies) => {
    dispatch({ type: 'SAVE_COMPANIES', companies });
  };

  const removeCompanies = () => {
    dispatch({ type: 'REMOVE_COMPANIES' });
  };

  const setActiveCompany = (companyId, persist = false) => {
    dispatch({ type: 'SET_ACTIVE_COMPANY', companyId });
    if (persist) {
      saveSettings({ defaultCompany: companyId }, true);
    }
  };

  const getActiveCompanySettings = React.useCallback(() => {
    const activeCompany = state.companies.find((company) => company.id === state.activeCompany);
    return activeCompany?.settings || defaultJobSettings;
  }, [state.companies, state.activeCompany]);

  const fetchCompanyCustomers = async (
    companyId = state.activeCompany,
    select = 'name',
    orderby = 'name',
    order = 'asc'
  ) => {
    try {
      const response = await axios.get(
        `/customers/?$top=-1&$cid=${companyId}&$select=${select}&$orderby=${orderby}&$order=${order}`
      );

      if (response.status === 200) {
        return response.data.value;
      }
    } catch (err) {
      console.error(err);
    }

    return [];
  };

  const fetchCompanyCategories = async (companyId = state.activeCompany, select = 'name') => {
    try {
      const response = await axios.get(
        `/jobs/categories/?$top=-1&$cid=${companyId}&$select=${select}`
      );

      if (response.status === 200) {
        return response.data.value;
      }
    } catch (err) {
      console.error(err);
    }

    return [];
  };

  return (
    <CompanyContext.Provider
      value={{
        state,
        dispatch,
        userState,
        companiesOptions,
        fetchCompanies,
        saveCompanies,
        removeCompanies,
        setActiveCompany,
        getActiveCompanySettings,
        defaultCompany,
        fetchCompanyCustomers,
        fetchCompanyCategories,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

CompanyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
